import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Result, Alert } from "antd";
import CustomButton from "Components/CustomButton";
import Spin from "Components/Spinner";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import SelectPaymentMethodModal from "../../../Shared/SelectPaymentMethodModal";
import ErrorLoadingPayPal from "./ErrorLoadingPayPal";
import CheckingPayment from "./CheckingPayment";

import { purchaseProductsTotalFloat } from "utils/helper";
import { ReloadOutlined } from "@ant-design/icons";
import ButtonWrapper from "./ButtonWrapper";
import { utmParams } from "utils/helper";
import apiHandler from "utils/apiHandler";
import { isNil, isNilOrEmpty, isNilTextOrEmpty } from "@salespark/toolkit";
const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } = utmParams();

//https://paypal.github.io/react-paypal-js/?path=/docs/example-paypalbuttons--default
//https://codesandbox.io/s/sandpack-project-forked-j5pgvj?file=/App.js:1820-2066
// This values are the props in the UI
const currency = "EUR";

/***********************************************************************************************
 ***********************************************************************************************
 ***********************************************************************************************
 * ##: APP
 ***********************************************************************************************
 ***********************************************************************************************
 **********************************************************************************************/
const PayPal = ({ productData, data, actionHandler, step, customStyle, setShowChangeMethod }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectPaymentMethod, setSelectPaymentMethod] = useState({ visible: false });
  const [paymentStatus, setPaymentStatus] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [waitForPayment, setWaitForPayment] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(undefined);
  const [errorFetching, setErrorFetching] = useState(false);
  const [paid, setPaid] = useState(false);

  const paymentDataSetRef = useRef(false);

  const [buttonCreated, setButtonCreated] = useState(false);

  const totalAmount = purchaseProductsTotalFloat(data.purchase_products);

  useEffect(() => {
    const status = data?.status?.action;
    setPaid(status === "paid");

    if (paypalClientId === undefined) getPayPalClientId();
  }, []);

  useEffect(() => {
    // Only show the "change method" link once the PayPal button is ready (not loading).
    const show = paypalClientId !== undefined && !errorFetching && paymentStatus?.status === undefined && !paid && !loading;
    if (setShowChangeMethod) setShowChangeMethod(show);
    return () => {
      if (setShowChangeMethod) setShowChangeMethod(false);
    };
  }, [paypalClientId, errorFetching, paymentStatus?.status, paid, loading]);

  const getPurchaseUnits = (purchase_id) => {
    if (data?.purchase_products === undefined) return;
    let total = purchaseProductsTotalFloat(data.purchase_products).toFixed(2);

    return [
      {
        custom_id: purchase_id,
        amount: {
          currency_code: "EUR",
          value: total,
          breakdown: {
            item_total: {
              currency_code: "EUR",
              value: total,
            },
          },
        },
        items: [...getItems()],
      },
    ];
  };

  const getItems = () => {
    const items = [];
    data.purchase_products.forEach((product) => {
      items.push({
        sku: product.product_id,
        name: product.name, //  ` x ${product.name}`

        unit_amount: {
          currency_code: "EUR",
          value: product.price,
        },
        quantity: String(product.qty || 1),
      });
    });
    return items;
  };

  const getPayPalClientId = async () => {
    if (buttonCreated === true) {
      return;
    }

    setLoading(true);
    setPaypalClientId(undefined);

    const clientIdResponse = await apiHandler.getOne(`/payments/paypal/clientid/${productData.product_id}`);
    if (clientIdResponse.status === false) {
      setLoading(false);
      setErrorFetching(true);
      return;
    }

    const clientId = clientIdResponse.data.clientId;
    if (isNil(clientId)) {
      setLoading(false);
      setErrorFetching(true);
      return;
    }
    const response = await createPayPalButton();
    setButtonCreated(true);
    if (response.status) {
      setErrorFetching(false);
      setPaypalClientId(clientId);
    }
  };

  const createPayPalButton = (force = false) => {
    if (data?.identification?.email === undefined || data?.identification?.email === null) {
      return;
    }

    return new Promise(async (resolve) => {
      if (buttonCreated === true && force === false) {
        resolve({ status: true, data: {} });
        return;
      }

      setPaymentStatus({});
      setPaymentFailed(false);
      setErrorFetching(false);
      setLoading(true);
      const payload = {
        buyer: data.identification,
        products: data.purchase_products,
        purchase_id: data.purchase_id,
        main_product: data.main_product,
        coupon: data.coupon,
        pathname: window.location.pathname,
        origin: window.location.origin,
        affiliate_id: data.affiliate_id,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        free_resolution_waiver: data.identification?.free_resolution_checkbox || false,
        is_offer: data.identification?.is_offer || false,
        offer_info: {
          gift_name: data.identification?.gift_name || "",
          gift_email: data.identification?.gift_email || "",
          gift_message: data.identification?.gift_message || "",
          gift_delivery_date: data.identification?.gift_delivery_date || "",
        },
      };

      // Disable hability to change quantity
      actionHandler("qty_locked", true);

      // Create Order
      const order_response = await apiHandler.post(`/orders`, { ...payload, method: "paypal", source: "checkout" });
      // Check if there is an error
      if (!order_response.status) {
        setErrorFetching(true);
        resolve({ status: false, data: {} });
        return;
      }

      resolve({ status: true, data: {} });
    });
  };

  const paymentCompleted = (order) => {
    actionHandler("update_status", {
      action: "wait-for-payment",
      data: {
        method_id: "PayPal",
        entity: "00000",
        reference: order.orderID,
        payment_unique_id: paymentData.payment_unique_id,
        // purchase_id: order.purchase_id,
      },
    });
    setWaitForPayment(true);
    //  console.log("paymentCompleted orderID", orderID);
  };

  const createPaymentIntent = (orderID) => {
    return new Promise(async (resolve) => {
      try {
        const payload = {
          buyer: data.identification,
          products: data.purchase_products,
          purchase_id: data.purchase_id,
          main_product: data.main_product,
          reference: orderID,
        };

        //const create_intent_response = await _createPaymentIntent(payload);
        const create_intent_response = await apiHandler.post(`/payments/paypal/intent/create/v3`, payload);
        setLoading(false);
        // Check if there is an error
        if (!create_intent_response.status) {
          setErrorFetching(true);
          resolve({ status: false, data: {} });
          return;
        }

        if (!paymentDataSetRef.current) {
          setPaymentData(create_intent_response.data);
          paymentDataSetRef.current = true;
        }

        // Resolve promise
        resolve({ status: true, data: {} });

        // Error handling
      } catch (error) {
        //console.log("error", error);
        setErrorFetching(true);
        resolve({ status: false, data: error });
      }
    });
  };

  const bwOnInit = async (data) => {
    // console.log("ButtonWrapper onInit", data);
    await createPaymentIntent(data.correlationID);
  };
  const bwOnCreateOrder = async (orderId) => {
    // console.log("ButtonWrapper onCreateOrder", orderId);
    await createPaymentIntent(orderId);
  };
  const bwOnError = (data) => {
    // console.log("ButtonWrapper onError", data);
    setPaymentStatus({ status: "error", data: data });
    setPaymentFailed(true);
  };

  const bwOnCancel = (data, actions) => {
    // console.log("ButtonWrapper onCancel", data, actions);
    setPaymentStatus({ status: "cancel", data: data });
    setPaymentFailed(true);
  };
  const bwOnApprove = async (data, actions, details) => {
    //  alert("Approve: " + JSON.stringify(data, 0, 2));
    // const name = details.payer.name.given_name;
    //console.log("onApprove orderID", data.orderID);
    paymentCompleted(data);
    setPaymentStatus({ status: "approve", data, details });
  };

  return (
    <div className="paypal-container">
      <div className="payment-data-container">
        {paid === false && (
          <Spin spinning={loading} tip={t("paypal.loadingTip")}>
            {loading && <div className="loading"></div>}

            {paypalClientId !== undefined && !errorFetching && paymentStatus?.status === undefined && (
              <div className={loading ? "loading-blur" : ""}>
                <div className="button-helper">{t("paypal.buttonHelper")}</div>
                <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "EUR" }}>
                  <ButtonWrapper
                    amount={totalAmount}
                    currency={currency}
                    showSpinner={loading}
                    purchaseUnits={getPurchaseUnits(data?.purchase_id)}
                    onInit={bwOnInit}
                    onCreateOrder={bwOnCreateOrder}
                    onError={bwOnError}
                    onCancel={bwOnCancel}
                    onApprove={bwOnApprove}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            {paymentFailed && !loading && (
              <div className="payment-error">
                <div className="error">
                  {paymentStatus.status === "error" && (
                    <div className="alerts">
                      <Alert
                        message={t("paypal.paymentErrorTitle")}
                        description={t("paypal.paymentErrorDescription")}
                        type="error"
                        showIcon
                      />
                    </div>
                  )}
                  {paymentStatus.status === "cancel" && (
                    <div className="alerts">
                      <Alert
                        message={t("paypal.paymentCancelTitle")}
                        description={t("paypal.paymentCancelDescription")}
                        type="warning"
                        showIcon
                      />
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="primary" onClick={() => createPayPalButton(true)}>
                    {t("paypal.retryButton")} <ReloadOutlined />
                  </CustomButton>
                </div>
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="link" onClick={() => setSelectPaymentMethod({ visible: true })}>
                    {t("paypal.changeMethodButton")}
                  </CustomButton>
                </div>
              </div>
            )}

            {errorFetching && !loading && (
              <ErrorLoadingPayPal
                createPayPalButton={() => createPayPalButton(true)}
                setSelectPaymentMethod={setSelectPaymentMethod}
                paypalClientId={paypalClientId}
                customStyle={customStyle}
              />
            )}

            {waitForPayment && !errorFetching && paymentStatus.status === "approve" && <CheckingPayment />}
          </Spin>
        )}
      </div>

      {paid === true && <Result status="success" title={t("paypal.paidTitle")} subTitle={t("paypal.paidSubtitle")} />}
      <SelectPaymentMethodModal
        data={data}
        actionHandler={actionHandler}
        visible={selectPaymentMethod.visible}
        close={() => setSelectPaymentMethod({ visible: false })}
        customStyle={customStyle}
      />
    </div>
  );
};

export default PayPal;
